import React, { useMemo, type ComponentPropsWithoutRef, type PropsWithChildren } from "react";

import { type Floor } from "@eisox/api/core-api";
import { Button, cn, Select as DSSelect, SearchField, type SearchFieldProps } from "@eisox/design-system";
import { ArrowRightIcon, CrossIcon } from "@eisox/icons";
import { createContext } from "@radix-ui/react-context";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { Tooltip } from "~/components/tooltip";
import type { AvailableRoomTypes } from "~/helpers/room.helper";
import { m } from "~/paraglide/messages";
import type { EnhancedRoom } from "~/types/room.type";

import { filterRooms, type Filters } from "../helpers";
import {
  ParametersSearch as ParametersSearchComponent,
  type ParametersSearchProps as ParametersSearchComponentProps,
} from "./parameters-search";
import { RoomsSelectorDialog, type RoomSelectorDialogProps } from "./rooms-selector-dialog";

/* -------------------------------------------------------------------------------------------------
 * RoomsSelector
 * -----------------------------------------------------------------------------------------------*/

interface RoomsSelectorContextValue {
  rooms: EnhancedRoom[];
  selectedRooms: EnhancedRoom[];
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  personalized: boolean;
}

const [RoomsSelectorContextProvider, useRoomsSelectorContext] =
  createContext<RoomsSelectorContextValue>("RoomsSelector");

interface RoomsSelectorProps extends PropsWithChildren {
  rooms: EnhancedRoom[];
  value?: Filters;
  onValueChange?: (filters: Filters) => void;
  onFilteredRoomsChange?: (rooms: EnhancedRoom[]) => void;
  defaultValue?: Filters;
}

const Root: React.FC<RoomsSelectorProps> = ({
  rooms,
  children,
  value: filtersProp,
  onValueChange,
  onFilteredRoomsChange,
  defaultValue = {},
}) => {
  const [filters, setFilters] = useControllableState({
    prop: filtersProp,
    onChange: filters => {
      onValueChange?.(filters);
      onFilteredRoomsChange?.(filterRooms(rooms, filters));
    },
    defaultProp: defaultValue,
  });

  const selectedRooms: EnhancedRoom[] = filterRooms(rooms, filters);
  const personalized = !!filters.personalizedRoomIds?.length;

  return (
    <RoomsSelectorContextProvider
      rooms={rooms}
      selectedRooms={selectedRooms}
      filters={filters}
      setFilters={setFilters}
      personalized={personalized}
    >
      {children}
    </RoomsSelectorContextProvider>
  );
};

Root.displayName = "RoomsSelector";

/* -------------------------------------------------------------------------------------------------
 * TextSearch
 * -----------------------------------------------------------------------------------------------*/

const TEXT_SEARCH_NAME = "TextSearch";

type TextSearchProps = Omit<SearchFieldProps, "value" | "onValueChange" | "defaultValue">;

const TextSearch = React.forwardRef<HTMLInputElement, TextSearchProps>(({ placeholder, disabled, ...props }, ref) => {
  const { filters, setFilters, personalized } = useRoomsSelectorContext(TEXT_SEARCH_NAME);

  return (
    <SearchField
      {...props}
      ref={ref}
      value={filters.textSearch ?? ""}
      onValueChange={textSearch => setFilters(prev => ({ ...prev, textSearch }))}
      placeholder={placeholder ?? `${m.room()}, ${m.floor()}, ${m.type()}`}
      disabled={disabled ?? personalized}
    />
  );
});

TextSearch.displayName = TEXT_SEARCH_NAME;

/* -------------------------------------------------------------------------------------------------
 * TypeSearch
 * -----------------------------------------------------------------------------------------------*/

const TYPE_SEARCH_NAME = "TypeSearch";

interface TypeSearchProps {
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const TypeSearch: React.FC<TypeSearchProps> = ({ disabled, className, style }) => {
  const { rooms, filters, setFilters, personalized } = useRoomsSelectorContext(TYPE_SEARCH_NAME);

  const typeValues = useMemo(() => new Set(rooms.map(r => r.type)), [rooms]);

  const options = useMemo(
    () =>
      [...typeValues]
        .map(type => ({
          value: type,
          name: m.room_type({ type: type as (typeof AvailableRoomTypes)[number] }),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [typeValues],
  );

  return (
    <Select
      value={filters.types ?? []}
      onValueChange={types => setFilters(prev => ({ ...prev, types }))}
      placeholder={m.types()}
      options={options}
      disabled={disabled ?? personalized}
      className={className}
      style={style}
    />
  );
};

TypeSearch.displayName = TYPE_SEARCH_NAME;

/* -------------------------------------------------------------------------------------------------
 * PlanSearch
 * -----------------------------------------------------------------------------------------------*/

const PLAN_SEARCH_NAME = "PlanSearch";

interface PlanSearchProps {
  floors: Floor[];
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const PlanSearch: React.FC<PlanSearchProps> = ({ floors, disabled, className, style }) => {
  const { rooms, filters, setFilters, personalized } = useRoomsSelectorContext(PLAN_SEARCH_NAME);

  const planIds = useMemo(() => new Set(rooms.map(r => r.floor.id)), [rooms]);

  const options = useMemo(
    () =>
      [...planIds]
        .map(id => {
          const plan = floors.find(p => p.id === id);
          return { value: id, name: plan?.name ?? "-" };
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [planIds, floors],
  );

  return (
    <Select
      value={filters.plans ?? []}
      onValueChange={plans => setFilters(prev => ({ ...prev, plans }))}
      options={options}
      placeholder={m.floors()}
      disabled={disabled ?? personalized}
      className={className}
      style={style}
    />
  );
};

PlanSearch.displayName = PLAN_SEARCH_NAME;

/* -------------------------------------------------------------------------------------------------
 * GroupSearch
 * -----------------------------------------------------------------------------------------------*/

const GROUP_SEARCH_NAME = "GroupSearch";

interface GroupSearchProps {
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const GroupSearch: React.FC<GroupSearchProps> = ({ disabled, className, style }) => {
  const { rooms, filters, setFilters, personalized } = useRoomsSelectorContext(GROUP_SEARCH_NAME);

  const groups = useMemo(() => new Set(rooms.flatMap(room => room.groups)), [rooms]);

  const options = useMemo(() => Array.from(groups).map(group => ({ value: group, name: group })), [groups]);

  return (
    <Select
      value={filters.groups ?? []}
      onValueChange={groups => setFilters(prev => ({ ...prev, groups }))}
      options={options}
      placeholder={m.groups()}
      disabled={disabled ?? personalized}
      className={className}
      style={style}
    />
  );
};

GroupSearch.displayName = GROUP_SEARCH_NAME;

/* -------------------------------------------------------------------------------------------------
 * ParametersSearch
 * -----------------------------------------------------------------------------------------------*/

const PARAMETERS_SEARCH_NAME = "ParametersSearch";

type ParametersSearchProps = Pick<
  ParametersSearchComponentProps,
  "disabled" | "className" | "showSoundSensorFilter" | "showCovSensorFilter" | "frostFreePeriodsAllowed"
>;

const ParametersSearch: React.FC<ParametersSearchProps> = ({
  className,
  disabled,
  showSoundSensorFilter,
  showCovSensorFilter,
  frostFreePeriodsAllowed,
}) => {
  const { filters, setFilters, personalized } = useRoomsSelectorContext(PARAMETERS_SEARCH_NAME);

  return (
    <ParametersSearchComponent
      className={className}
      unusualAbsenceDetection={filters.parameters?.unusualAbsenceDetection ?? null}
      onUnsualAbsenceDetectionChange={value =>
        setFilters(prev => ({ ...prev, parameters: { ...prev.parameters, unusualAbsenceDetection: value } }))
      }
      precomfortRangeGeneration={filters.parameters?.precomfortRangeGeneration ?? null}
      onPrecomfortRangeGenerationChange={value =>
        setFilters(prev => ({ ...prev, parameters: { ...prev.parameters, precomfortRangeGeneration: value } }))
      }
      buttonSettings={filters.parameters?.buttonSettings ?? null}
      onButtonSettingsChange={value =>
        setFilters(prev => ({ ...prev, parameters: { ...prev.parameters, buttonSettings: value } }))
      }
      frostFreeMode={filters.parameters?.frostFreeMode ?? null}
      frostFreePeriodsAllowed={frostFreePeriodsAllowed}
      onFrostFreeModeChange={value =>
        setFilters(prev => ({ ...prev, parameters: { ...prev.parameters, frostFreeMode: value } }))
      }
      comfortTemperature={filters.parameters?.comfortTemperature ?? null}
      onComfortTemperatureChange={value =>
        setFilters(prev => ({ ...prev, parameters: { ...prev.parameters, comfortTemperature: value } }))
      }
      nightTemperature={filters.parameters?.nightTemperature ?? null}
      onNightTemperatureChange={value =>
        setFilters(prev => ({ ...prev, parameters: { ...prev.parameters, nightTemperature: value } }))
      }
      soundSensorParam={filters.parameters?.soundSensorParam ?? null}
      onSoundSensorParamChange={value =>
        setFilters(prev => ({ ...prev, parameters: { ...prev.parameters, soundSensorParam: value } }))
      }
      showSoundSensorFilter={showSoundSensorFilter}
      covSensorParam={filters.parameters?.covSensorParam ?? null}
      onCovSensorParamChange={value =>
        setFilters(prev => ({ ...prev, parameters: { ...prev.parameters, covSensorParam: value } }))
      }
      showCovSensorFilter={showCovSensorFilter}
      disabled={disabled ?? personalized}
    />
  );
};

ParametersSearch.displayName = PARAMETERS_SEARCH_NAME;

/* -------------------------------------------------------------------------------------------------
 * PersonnalizedSearch
 * -----------------------------------------------------------------------------------------------*/

const PERSONNALIZED_SEARCH_NAME = "PersonnalizedSearch";

type PersonnalizedSearchProps = Omit<RoomSelectorDialogProps, "value" | "onValueChange" | "rooms">;

const PersonnalizedSearch: React.FC<PersonnalizedSearchProps> = ({ floors, ...props }) => {
  const { rooms, filters, setFilters } = useRoomsSelectorContext(PERSONNALIZED_SEARCH_NAME);
  const personalizedRoomIds = filters.personalizedRoomIds;

  const handleChangePersonalizedRoomIds = (personalizedRoomIds: string[]) => {
    setFilters(prev => ({ ...prev, personalizedRoomIds }));
  };

  return (
    <RoomsSelectorDialog
      {...props}
      floors={floors}
      rooms={rooms}
      value={personalizedRoomIds}
      onValueChange={handleChangePersonalizedRoomIds}
    >
      <Button>
        {m.personalize()}
        <ArrowRightIcon />
      </Button>
    </RoomsSelectorDialog>
  );
};

PersonnalizedSearch.displayName = PERSONNALIZED_SEARCH_NAME;

/* -------------------------------------------------------------------------------------------------
 * Result
 * -----------------------------------------------------------------------------------------------*/

const RESULT_NAME = "Result";

type ResultProps = ComponentPropsWithoutRef<"div">;

const Result: React.FC<ResultProps> = ({ className, ...props }) => {
  const { rooms, selectedRooms, personalized, setFilters } = useRoomsSelectorContext(RESULT_NAME);

  const allSelected = rooms.length === selectedRooms.length;
  const resetable = selectedRooms.length > 0;

  const handleReset = () => setFilters({});

  const count = selectedRooms.length;
  const gender = "f" as const;
  const selectedLabel = m.selected_count({ count, gender });
  const label = personalized
    ? `${m.personalized()}: ${selectedLabel}`
    : allSelected
      ? m.all_selected({ gender })
      : selectedLabel;

  return (
    <div {...props} className={cn("flex items-center gap-[5px] text-base", className)}>
      <Tooltip
        content={
          selectedRooms.length > 0 && (
            <div className="flex flex-col">
              {selectedRooms.map(r => (
                <span key={r.id}>{r.name}</span>
              ))}
            </div>
          )
        }
        keepHoverOpen={true}
      >
        <p>{label}</p>
      </Tooltip>
      {resetable && <CrossIcon className="h-[10px] w-[10px] cursor-pointer" onClick={handleReset} />}
    </div>
  );
};

Result.displayName = RESULT_NAME;

interface SelectProps {
  value: string[];
  onValueChange: (value: string[]) => void;
  options: { value: string; name: string }[];
  placeholder: string;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const Select: React.FC<SelectProps> = ({ value, onValueChange, options, placeholder, disabled, className, style }) => (
  <DSSelect.Root multiple value={value} onValueChange={onValueChange}>
    <DSSelect.Trigger disabled={disabled} className={className} style={style}>
      <DSSelect.Value placeholder={placeholder} />
    </DSSelect.Trigger>
    <DSSelect.Content className="w-fit max-w-[300px]" align="start">
      <DSSelect.Input />
      <DSSelect.Viewport>
        {options.map(option => (
          <DSSelect.Item key={option.value} value={option.value}>
            {option.name}
          </DSSelect.Item>
        ))}
        <DSSelect.Empty>{m.no_result()}</DSSelect.Empty>
      </DSSelect.Viewport>
    </DSSelect.Content>
  </DSSelect.Root>
);

export { GroupSearch, ParametersSearch, PersonnalizedSearch, PlanSearch, Result, Root, TextSearch, TypeSearch };
export type {
  GroupSearchProps,
  ParametersSearchProps,
  PersonnalizedSearchProps,
  PlanSearchProps,
  ResultProps,
  RoomsSelectorProps,
  TextSearchProps,
  TypeSearchProps,
};
